import { useState } from "react";
import {
  tokens,
  cardBase,
  sectionLabelStyle,
  Card,
  CardHeader,
  Badge,
  Th,
  Td,
  StatusPill,
  FooterNote,
  PrimaryBtn,
  MetricCard,
  boldCellStyle,
} from "./dashboardKit";

// ============================================================
// Data
// ============================================================

const KPIS = [
  {
    badge: "QUERY",
    title: "Total AP Outstanding",
    value: "AED 3.2M",
    sub: "All vendors · Avg days to pay: 38",
  },
  {
    badge: "QUERY",
    title: "Overdue Payables",
    value: "AED 640,000",
    sub: "↓ from AED 790,000 last month",
  },
  {
    badge: "QUERY",
    title: "Early Payment Discounts Captured",
    value: "AED 120,000",
    sub: "Last 90 days · Potential missed: AED 40,000",
  },
];

const AGING_BUCKETS = [
  { bucket: "0–30 days", amount: 1900000, pct: 59, barColor: "#3B82F6" },
  { bucket: "31–60 days", amount: 750000, pct: 23, barColor: "#60A5FA" },
  { bucket: "61–90 days", amount: 350000, pct: 11, barColor: "#FBBF24" },
  {
    bucket: "> 90 days",
    amount: 200000,
    pct: 7,
    barColor: "#EF4444",
    danger: true,
  },
];

const VENDORS = [
  {
    name: "Alpha Logistics",
    category: "Logistics",
    ap: 410000,
    risk: "High",
    strategy: "Keep current terms",
  },
  {
    name: "BrightTech IT",
    category: "IT services",
    ap: 290000,
    risk: "Medium",
    strategy: "Negotiate volume discount",
  },
  {
    name: "MetroRent",
    category: "Facilities",
    ap: 260000,
    risk: "Low",
    strategy: "Explore early-pay discount",
  },
  {
    name: "GreenSupplies",
    category: "Consumables",
    ap: 180000,
    risk: "Low",
    strategy: "Consolidate shipments",
  },
  {
    name: "CloudHost",
    category: "Cloud infra",
    ap: 160000,
    risk: "Medium",
    strategy: "Optimize licenses",
  },
];

const FUNNEL = [
  { stage: "Requested", count: 64, avgTime: "0.5 days", bottleneck: "No" },
  {
    stage: "Manager approval",
    count: 28,
    avgTime: "2.1 days",
    bottleneck: "Maybe",
  },
  {
    stage: "Finance review",
    count: 19,
    avgTime: "3.4 days",
    bottleneck: "Yes",
  },
  { stage: "Ready to pay", count: 13, avgTime: "1.0 days", bottleneck: "No" },
];

const INITIAL_DISCOUNTS = [
  {
    vendor: "MetroRent",
    invoice: "#A1043",
    due: "05 Jan",
    discount: "2% if paid by 20 Dec",
    decision: "Accept",
  },
  {
    vendor: "CloudHost",
    invoice: "#INV-8821",
    due: "12 Jan",
    discount: "1.5% if paid by 22 Dec",
    decision: "Review",
  },
  {
    vendor: "GreenSupplies",
    invoice: "#GS-3342",
    due: "18 Dec",
    discount: "1% if paid by 10 Dec",
    decision: "Accept",
  },
];

const gridTwo = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
  gap: 16,
  marginBottom: 16,
};

// ============================================================
// Component
// ============================================================

export default function Payables() {
  const [discounts, setDiscounts] = useState(INITIAL_DISCOUNTS);

  function cycleDecision(idx) {
    setDiscounts((prev) =>
      prev.map((row, i) => {
        if (i !== idx) return row;
        const order = ["Accept", "Review", "Declined"];
        const next = order[(order.indexOf(row.decision) + 1) % order.length];
        return { ...row, decision: next };
      }),
    );
  }

  return (
    <div className="py-1">
      {/* ── At a glance ── */}

      <div
        style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 24 }}
      >
        {KPIS.map((k) => (
          <MetricCard
            key={k.title}
            badge={k.badge}
            title={k.title}
            value={k.value}
            sub={k.sub}
          />
        ))}
      </div>

      {/* ── Working capital detail ── */}
      <div style={sectionLabelStyle}>Working capital detail</div>
      <div style={gridTwo}>
        {/* AP Aging by Bucket */}
        <Card>
          <CardHeader
            title="AP aging by bucket"
            sub="Distribution of outstanding payables"
            badge={<Badge type="QUERY" />}
          />
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <Th>Bucket</Th>
                <Th>Amount (AED)</Th>
                <Th>% of AP</Th>
                <Th>Share</Th>
              </tr>
            </thead>
            <tbody>
              {AGING_BUCKETS.map((row) => (
                <tr key={row.bucket}>
                  <Td>{row.bucket}</Td>
                  <Td style={boldCellStyle(row.danger)}>
                    {row.amount.toLocaleString("en-US")}
                  </Td>
                  <Td style={{ color: tokens.mutedColor }}>{row.pct}%</Td>
                  <Td>
                    <div
                      style={{
                        height: 6,
                        width: 96,
                        overflow: "hidden",
                        borderRadius: 999,
                        background: "#F1F5F9",
                      }}
                    >
                      <div
                        style={{
                          height: "100%",
                          borderRadius: 999,
                          background: row.barColor,
                          width: `${Math.max(row.pct, 4)}%`,
                        }}
                      />
                    </div>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
          <FooterNote>
            <span>
              Target: reduce &gt;60 days bucket below 10% within 60 days.
            </span>
          </FooterNote>
        </Card>

        {/* Top Vendors by Exposure */}
        <Card>
          <CardHeader
            title="Top vendors by exposure"
            sub="Ranked by exposure · AI-suggested strategy"
            badge={<Badge type="AI" />}
          />
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <Th>Vendor</Th>
                <Th>Category</Th>
                <Th>AP (AED)</Th>
                <Th>Risk</Th>
                <Th>Strategy</Th>
              </tr>
            </thead>
            <tbody>
              {VENDORS.map((v) => (
                <tr key={v.name}>
                  <Td style={boldCellStyle()}>{v.name}</Td>
                  <Td style={{ color: tokens.mutedColor }}>{v.category}</Td>
                  <Td>{v.ap.toLocaleString("en-US")}</Td>
                  <Td>
                    <StatusPill level={v.risk} />
                  </Td>
                  <Td style={{ color: tokens.mutedColor, fontSize: 12 }}>
                    {v.strategy}
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      </div>

      <div style={gridTwo}>
        {/* Approval Funnel */}
        <Card>
          <CardHeader
            title="Approval funnel"
            sub="From request to payment"
            badge={<Badge type="QUERY" />}
          />
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <Th>Stage</Th>
                <Th>Count</Th>
                <Th>Avg. time</Th>
                <Th>Bottleneck?</Th>
              </tr>
            </thead>
            <tbody>
              {FUNNEL.map((row) => (
                <tr key={row.stage}>
                  <Td style={boldCellStyle()}>{row.stage}</Td>
                  <Td>{row.count}</Td>
                  <Td style={{ color: tokens.mutedColor }}>{row.avgTime}</Td>
                  <Td>
                    <StatusPill level={row.bottleneck} />
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
          <FooterNote>
            <span>
              Configure auto-approvals for &lt; AED 10k and low-risk vendors.
            </span>
            <PrimaryBtn>Edit approval rules</PrimaryBtn>
          </FooterNote>
        </Card>

        {/* Discount Opportunities */}
        <Card>
          <CardHeader
            title="Discount opportunities"
            sub="Early payment & dynamic discounting"
            badge={<Badge type="AI" />}
          />
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <Th>Vendor</Th>
                <Th>Invoice</Th>
                <Th>Due</Th>
                <Th>Discount</Th>
                <Th>Decision</Th>
              </tr>
            </thead>
            <tbody>
              {discounts.map((row, idx) => (
                <tr key={row.invoice}>
                  <Td style={boldCellStyle()}>{row.vendor}</Td>
                  <Td style={{ color: tokens.mutedColor }}>{row.invoice}</Td>
                  <Td style={{ color: tokens.mutedColor }}>{row.due}</Td>
                  <Td style={{ color: tokens.mutedColor }}>{row.discount}</Td>
                  <Td>
                    <StatusPill
                      level={row.decision}
                      onClick={() => cycleDecision(idx)}
                    />
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
          <FooterNote>
            <span>Align discount acceptance with cashflow scenarios.</span>
            <PrimaryBtn>Simulate impact</PrimaryBtn>
          </FooterNote>
        </Card>
      </div>
    </div>
  );
}
